// React
import React, { useCallback, useEffect, useMemo, useState } from 'react';
// External libs
import { get } from 'lodash';
// Redux
import { useDispatch } from 'react-redux';
// Material UI
import { IconButton, InputAdornment } from '@material-ui/core';
import { Visibility, VisibilityOff } from '@material-ui/icons';
// Interfaces
import { AvailableCountry } from '../../../../interfaces';
// Constants
import { InputType } from '../../../../constants/input-type';
import { textIds } from './consts';
// Utils
import StringUtils from '../../../../utils/StringUtils';
// Services
import CountriesService from '../../../../services/CountriesService';
import LocalStorageService from '../../../../services/local-storage-service';
// Tools
import { PWEXUtils } from '../../../../exceptions';
import { useFormatMessage } from '../../../../intl/intl';
// Actions
import AppActions from '../../../../redux/actions/app-actions';
import rootActions from '../../../../redux/actions/root.actions';
import EventActions from '../../../../redux/actions/event-actions/event.actions';
// Components
import { CountrySelector } from '../../../common/country-selector';
import CustomButton, {
  Colors,
  Types,
  Variants,
} from '../../../common/buttons/custom-button';
import CustomRadioCheck from '../../../common/custom-radio-check';
import CustomTextField from '../../../common/custom-text-field';
// Styles
import useStyles from './styles';
import { CustomLink } from '../../../common/custom-link';
import Typography, {
  Variants as Variant,
  FontWeight,
} from '../../../common/typography';

const Form = ({ handleShowForm }: any) => {
  const classes = useStyles();

  const emailLabelInput = useFormatMessage(textIds.emailLabelInput);
  const passwordLabelInput = useFormatMessage(textIds.passwordLabelInput);
  const title = useFormatMessage(textIds.title);
  const rememberMeText = useFormatMessage(textIds.rememberMeText);
  const submitButtonText = useFormatMessage(textIds.submitButtonText);
  const forgotPasswordText = useFormatMessage(textIds.forgotPassword);
  const createAccountText = useFormatMessage(textIds.createAccount);
  const createNowText = useFormatMessage(textIds.createNow);

  const [availableCountries, setAvailableCountries] = useState<
    AvailableCountry[]
  >([]);

  const [didFetchingFail, setDidFetchingFail] = useState<boolean>(false);
  const [email, setEmail] = useState<string | null>(null);
  const [helperText, setHelperText] = useState<string | null>(null);
  const [isCountrySelectorLoading, setIsCountrySelectorLoading] = useState<
    boolean
  >(false);
  const [password, setPassword] = useState<string | null>(null);
  const [remember, setRemember] = useState<boolean>(false);
  const [
    selectedCountry,
    setSelectedCountry,
  ] = useState<AvailableCountry | null>(null);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  const dispatch = useDispatch();

  /**
   * Sets in the component state all available countries from server.
   */
  const getAllAvailableCountries = useCallback(async () => {
    setIsCountrySelectorLoading(true);
    try {
      const countries = await CountriesService.getAllAvailable();
      setAvailableCountries(countries);
    } catch (ex) {
      await dispatch(AppActions.handleCatch(ex, true, false));
      const exMessage = PWEXUtils.getMessage(ex);
      setDidFetchingFail(true);
      setHelperText(exMessage);
    }
    setIsCountrySelectorLoading(false);
  }, [dispatch]);

  // Set initial effect
  useEffect(() => {
    getAllAvailableCountries();
  }, [getAllAvailableCountries]);

  const resolveCountry = useCallback(
    async countryToResolve => {
      // CountryToResolve cannot be used from useState, due to resolveCountry(useCallback) => handleCountrySelectorChange(useCallback)
      setIsCountrySelectorLoading(true);
      try {
        const lat = get(countryToResolve, 'lat', null);
        const lng = get(countryToResolve, 'lng', null);

        if (!lat || !lng) {
          return;
        }

        const response = await CountriesService.resolveCountry(lat, lng);
        LocalStorageService.setCountry(response.data);
        LocalStorageService.setLatAndLng(lat, lng);
        setDidFetchingFail(false);
        setHelperText(null);
      } catch (ex) {
        await dispatch(AppActions.handleCatch(ex, true, false));
        const exMessage = PWEXUtils.getMessage(ex);
        setDidFetchingFail(true);
        setHelperText(exMessage);
      }
      setIsCountrySelectorLoading(false);
    },
    [dispatch],
  );

  const mustInputsBeDisabled = useMemo(() => {
    return didFetchingFail || isCountrySelectorLoading || !selectedCountry;
  }, [didFetchingFail, isCountrySelectorLoading, selectedCountry]);

  const mustSubmitButtonBeDisabled = useMemo(() => {
    return (
      mustInputsBeDisabled ||
      StringUtils.isNullOrEmpty(email) ||
      StringUtils.isNullOrEmpty(password)
    );
  }, [mustInputsBeDisabled, email, password]);

  const handleCountrySelectorChange = useCallback(
    (e: React.ChangeEvent<{ name?: string; value: unknown }>) => {
      const selectedCode = get(e, 'target.value', null);

      const foundCountry = availableCountries.find(
        availableCountry => availableCountry.code === selectedCode,
      );

      if (!foundCountry) {
        return;
      }

      setSelectedCountry(foundCountry);

      setEmail(null);
      setPassword(null);

      resolveCountry(foundCountry);
    },
    [availableCountries, resolveCountry],
  );

  const handleClickShowPassword = (e: any) => {
    setShowPassword(!showPassword);
  };

  const handleEmailChange = useCallback((e: any) => {
    const value = get(e, 'currentTarget.value', null);
    setEmail(value);
  }, []);

  const handlePasswordChange = useCallback((e: any) => {
    const value = get(e, 'currentTarget.value', null);
    setPassword(value);
  }, []);

  const handleRemember = useCallback(
    (e: any) => {
      setRemember(!remember);
    },
    [remember],
  );

  const handleSubmit = useCallback(
    async (e: any) => {
      e.preventDefault();

      try {
        await dispatch(rootActions.login(email, password));
      } catch (ex) {
        await dispatch(AppActions.handleCatch(ex, true, false));
      }
    },
    [dispatch, email, password],
  );
  const handleClickShowPasswordPath = (e: any) => {
    handleShowForm(false);
  };

  const handleCreateNowAccount = useCallback(() => {
    EventActions.onLoginScreenSelection('self_onboarding');
    window.open(process.env.REACT_APP_NEW_ACCOUNT, '_blank');
  }, []);
  const visibilityEndorment = (
    <InputAdornment position='end'>
      <IconButton
        className={classes.visibilityIcon}
        edge='end'
        aria-label='Toggle password visibility'
        onClick={handleClickShowPassword}
      >
        {showPassword ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <div className={classes.root}>
      <h1 className={classes.title}>{title}</h1>
      <form className={classes.loginForm} autoComplete='on'>
        <CountrySelector
          error={didFetchingFail}
          helperText={helperText}
          isLoading={isCountrySelectorLoading}
          list={availableCountries}
          value={selectedCountry}
          onChange={handleCountrySelectorChange}
        />
        {/*Email text field*/}
        <CustomTextField
          classes={{ textFieldRoot: classes.textFieldContainer }}
          disabled={mustInputsBeDisabled}
          label={emailLabelInput}
          placeholder={'myEmail@rappi.com'}
          onChange={handleEmailChange}
          required={true}
          type={InputType.EMAIL}
          value={email}
          autoComplete='on'
        />
        {/*Password text field*/}
        <CustomTextField
          classes={{ textFieldRoot: classes.textFieldContainer }}
          disabled={mustInputsBeDisabled}
          endAdornment={visibilityEndorment}
          label={passwordLabelInput}
          placeholder={'1234example'}
          onChange={handlePasswordChange}
          required={true}
          type={showPassword ? InputType.TEXT : InputType.PASSWORD}
          value={password}
          autoComplete='off'
        />
        <div className={classes.linksContainer}>
          <CustomRadioCheck
            className={classes.rememberContainer}
            disabled={mustInputsBeDisabled}
            label={rememberMeText}
            onChange={handleRemember}
            value={remember}
          />
          <CustomLink
            classes={{ root: classes.forgotPassword }}
            color='primary'
            onClick={handleClickShowPasswordPath}
          >
            {forgotPasswordText}
          </CustomLink>
        </div>

        <CustomButton
          classes={{ root: classes.buttonRoot }}
          color={Colors.PRIMARY}
          disabled={mustSubmitButtonBeDisabled}
          onClick={handleSubmit}
          text={submitButtonText}
          type={Types.SUBMIT}
          variant={Variants.CONTAINED}
        />
        <div className={classes.boxContainer}>
          <Typography
            classes={{ root: classes.createAccount }}
            fontWeight={FontWeight.Regular}
            variant={Variant.Body}
          >
            {createAccountText}
          </Typography>
          <CustomLink
            classes={{ root: classes.createNow }}
            color='primary'
            onClick={handleCreateNowAccount}
          >
            {createNowText}
          </CustomLink>
        </div>
      </form>
    </div>
  );
};

export default Form;
